/**
 * Users Management Page
 * Admin interface for managing users with FULL CRUD + Animations
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, UserPlus, Shield } from 'lucide-react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import UserModal from '../../components/admin/UserModal';
import { useAuthStore } from '../../store/authStore';
import { useDataSourceStore } from '../../store/dataSourceStore';
import CompanyContextBanner from '../../components/layout/CompanyContextBanner';

interface Role {
  _id: string;
  name: string;
  slug: string;
}

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  phone?: string;
  roleIds: (string | Role)[]; // Can be IDs or populated objects
  companyId: string;
  createdAt: string;
}

const UsersPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const queryClient = useQueryClient();
  const currentUser = useAuthStore(state => state.user);

  // Get selected company for super admin filtering
  const { selectedCompany } = useDataSourceStore();
  const isSuperAdmin =
    currentUser?.roles?.includes('super-admin') || currentUser?.roles?.includes('super_admin');

  // Fetch users
  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['users', searchTerm, statusFilter, selectedCompany?._id],
    queryFn: async () => {
      const params: any = {
        search: searchTerm || undefined,
        status: statusFilter || undefined,
      };

      // Super admins can filter by selected company
      if (isSuperAdmin && selectedCompany?._id) {
        params.companyId = selectedCompany._id;
      }

      const response = await api.get('/api/v1/users', { params });
      return response.data;
    },
    staleTime: 0, // Always fetch fresh data
    gcTime: 0, // Don't cache (renamed from cacheTime in v5)
  });

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/api/v1/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('User deleted successfully! 🗑️');
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error?.message || 'Failed to delete user');
    },
  });

  const users = usersData?.data || [];

  const handleNewUser = () => {
    setSelectedUser(undefined);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = (user: User) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${user.firstName} ${user.lastName}? This action cannot be undone.`
      )
    ) {
      deleteMutation.mutate(user._id);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedUser(undefined);
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'badge badge-success',
      inactive: 'badge badge-secondary',
      suspended: 'badge badge-danger',
      pending: 'badge badge-warning',
    };
    return styles[status as keyof typeof styles] || 'badge';
  };

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">Error loading users: {error.message}</div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fade-in">
      {/* Company Context Banner */}
      <CompanyContextBanner showDataSource={false} className="animate-slide-down" />

      {/* Header with Animation */}
      <div className="flex items-center justify-between animate-slide-down delay-100">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">👥 Users</h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-1">
            {isSuperAdmin && selectedCompany
              ? `Managing users for ${selectedCompany.name}`
              : 'Manage user accounts and permissions'}
          </p>
        </div>
        <button
          onClick={handleNewUser}
          className="btn btn-primary flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
        >
          <Plus className="w-5 h-5" />
          Add User
        </button>
      </div>

      {/* Search and Filters with Animation */}
      <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-xl p-4 animate-slide-up">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="input pl-10 transition-all duration-200 focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="input w-48 transition-all duration-200 focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Users Table with Animations */}
      <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-xl overflow-hidden animate-slide-up delay-100">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="inline-block w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
            <p className="mt-4 text-secondary-600 dark:text-secondary-400">Loading users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <UserPlus size={64} className="mx-auto text-secondary-400 animate-bounce" />
            <h3 className="mt-6 text-2xl font-bold text-secondary-900 dark:text-white">
              No users found
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400 mt-2">
              {searchTerm || statusFilter
                ? 'Try adjusting your filters'
                : 'Get started by creating your first user'}
            </p>
            {!searchTerm && !statusFilter && (
              <button
                onClick={handleNewUser}
                className="btn-primary mt-6 transform hover:scale-105 transition-all duration-200"
              >
                <Plus className="w-5 h-5 mr-2 inline" />
                Add First User
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-secondary-200 dark:divide-secondary-700">
              <thead className="bg-secondary-50 dark:bg-secondary-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                    Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-secondary-500 dark:text-secondary-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-secondary-800 divide-y divide-secondary-200 dark:divide-secondary-700">
                {users.map((user: User, index: number) => (
                  <tr
                    key={user._id}
                    className="hover:bg-secondary-50 dark:hover:bg-secondary-700 transition-colors duration-150 animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold shadow-lg">
                          {user.firstName[0]}
                          {user.lastName[0]}
                        </div>
                        <div>
                          <div className="font-medium text-secondary-900 dark:text-white">
                            {user.firstName} {user.lastName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(user.status)}>{user.status}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {user.roleIds && user.roleIds.length > 0 ? (
                          user.roleIds.map((role, idx) => {
                            // Handle both string IDs and populated role objects
                            const roleName = typeof role === 'string' ? 'Unknown Role' : role.name;
                            const roleId = typeof role === 'string' ? role : role._id;

                            return (
                              <span
                                key={roleId + idx}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 rounded-md text-xs font-medium"
                              >
                                <Shield className="w-3 h-3" />
                                {roleName}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-secondary-400 text-xs italic">No roles</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-2 text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-all duration-200 transform hover:scale-110"
                          title="Edit user"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          disabled={user._id === currentUser?._id}
                          className="p-2 text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900 transition-all duration-200 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            user._id === currentUser?._id ? 'Cannot delete yourself' : 'Delete user'
                          }
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {users.length > 0 && (
        <div className="flex items-center justify-between bg-white dark:bg-secondary-800 shadow-lg rounded-xl p-4">
          <div className="text-sm text-secondary-600 dark:text-secondary-400">
            Showing{' '}
            <span className="font-medium text-secondary-900 dark:text-white">{users.length}</span>{' '}
            users
          </div>
          <div className="flex items-center gap-2">
            <button className="btn btn-secondary">Previous</button>
            <button className="btn btn-secondary">Next</button>
          </div>
        </div>
      )}

      {/* User Modal */}
      <UserModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        user={selectedUser}
        currentUserRoles={currentUser?.roles}
      />
    </div>
  );
};

export default UsersPage;
